(() => {
  const prefersReducedMotion = () =>
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class DropdownMenu extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        hover: 'is-hover',
        active: 'is-active',
        animate: 'show-from-bottom',
      };

      this.selectors = {
        header: 'shopify-section-header',
        background: '.header-background',
        topMenuItem: '.header__menu-item--top',
        menus: 'dropdown-menu, mega-menu',
        wrapper: '.list-menu',
        childAnimate: '.list-menu-dropdown > .list-menu',
      };

      this.timeout = null;
      this.animationTimeout = null;
      this.closeTimeout = null;
      this.activationId = 0;

      this.onMouseOverEvent = this.handleMouseover.bind(this);
      this.onMouseLeaveEvent = this.handleMouseleave.bind(this);

      this.addEventListener('focusin', this.onMouseOverEvent);
      this.addEventListener('mouseenter', this.onMouseOverEvent);
    }

    handleMouseover() {
      window.clearTimeout(this.closeTimeout);

      if (this.classList.contains(this.classes.active)) return;

      this.bindEvent();
      if (!this.header || !this.wrapper) return;

      this.removeClasses();
      this.addClasses();
      this.resetBackground();
      this.animateMenu();
    }

    handleMouseleave() {
      window.clearTimeout(this.closeTimeout);
      this.closeTimeout = window.setTimeout(() => {
        if (this.matches(':hover') || this.contains(document.activeElement)) return;
        this.forceClose();
      }, prefersReducedMotion() ? 0 : 180);
    }

    forceClose() {
      if (!this.header) return;

      this.reset();
      this.resetBackground();
      this.header.classList.remove(this.classes.hover);

      if (this.wrapper) {
        this.wrapper.removeEventListener('mouseleave', this.onMouseLeaveEvent);
      }
    }

    bindEvent() {
      this.header = this.header || document.getElementById(this.selectors.header);
      this.wrapper = this.wrapper || this.closest(this.selectors.wrapper);

      if (!this.wrapper) return;

      this.wrapper.removeEventListener('mouseleave', this.onMouseLeaveEvent);
      this.wrapper.addEventListener('mouseleave', this.onMouseLeaveEvent);
    }

    addClasses() {
      const isTopLevel = this.querySelector(this.selectors.topMenuItem);
      const activationId = ++this.activationId;
      let delay = 0;

      if (isTopLevel && !this.isHover()) {
        delay = this.closest('.header--top-center, .header--top-left') ? 200 : 150;
      }

      this.timeout = window.setTimeout(() => {
        if (this.activationId === activationId && this.isHover()) {
          this.classList.add(this.classes.active);
        }
      }, prefersReducedMotion() ? 0 : delay);

      this.header.classList.add(this.classes.hover);
    }

    removeClasses() {
      if (!this.header) return;

      const menus = this.header.querySelectorAll(this.selectors.menus);

      menus.forEach((menu) => {
        let found = false;
        const childMenus = menu.querySelectorAll('dropdown-menu');

        childMenus.forEach((element) => {
          if (element === this) found = true;
        });

        if (!found && typeof menu.reset === 'function') {
          menu.reset();
        }
      });
    }

    isHover() {
      return Boolean(this.header && this.header.classList.contains(this.classes.hover));
    }

    reset() {
      this.activationId += 1;
      this.classList.remove(this.classes.active);

      const childAnimate = this.querySelector(this.selectors.childAnimate);
      if (childAnimate) childAnimate.classList.remove(this.classes.animate);

      window.clearTimeout(this.timeout);
      window.clearTimeout(this.animationTimeout);
      window.clearTimeout(this.closeTimeout);
      this.timeout = null;
      this.animationTimeout = null;
      this.closeTimeout = null;
    }

    resetBackground() {
      if (!this.header) return;

      const background = this.header.querySelector(this.selectors.background);
      if (background) background.classList.remove(this.classes.active);
    }

    animateMenu() {
      const childAnimate = this.querySelector(this.selectors.childAnimate);
      if (!childAnimate) return;

      const activationId = this.activationId;
      this.animationTimeout = window.setTimeout(() => {
        if (this.activationId === activationId && this.classList.contains(this.classes.active)) {
          childAnimate.classList.add(this.classes.animate);
        }
      }, prefersReducedMotion() ? 0 : 150);
    }

    close() {
      this.forceClose();
    }
  }

  if (!customElements.get('dropdown-menu')) {
    customElements.define('dropdown-menu', DropdownMenu);
  }

  class MegaMenu extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        hover: 'is-hover',
        active: 'is-active',
        animate: 'show-from-bottom',
      };

      this.selectors = {
        header: 'shopify-section-header',
        background: '.header-background',
        dropdown: '.list-menu-dropdown',
        menus: 'dropdown-menu, mega-menu',
        wrapper: '.list-menu',
        childAnimate: '.mega-menu__item, .mega-menu__promo',
      };

      this.timeouts = [];
      this.closeTimeout = null;

      this.onMouseOverEvent = this.handleMouseover.bind(this);
      this.onMouseLeaveEvent = this.handleMouseleave.bind(this);

      this.addEventListener('focusin', this.onMouseOverEvent);
      this.addEventListener('mouseenter', this.onMouseOverEvent);
    }

    handleMouseover() {
      window.clearTimeout(this.closeTimeout);

      if (this.classList.contains(this.classes.active)) return;

      this.bindEvent();
      if (!this.header || !this.wrapper) return;

      this.removeClasses();
      this.addClasses();
      this.showSublist();
    }

    handleMouseleave() {
      window.clearTimeout(this.closeTimeout);
      this.closeTimeout = window.setTimeout(() => {
        if (this.matches(':hover') || this.contains(document.activeElement)) return;
        this.forceClose();
      }, prefersReducedMotion() ? 0 : 180);
    }

    forceClose() {
      if (!this.header) return;

      this.reset();
      this.resetBackground();
      this.header.classList.remove(this.classes.hover);

      if (this.wrapper) {
        this.wrapper.removeEventListener('mouseleave', this.onMouseLeaveEvent);
      }
    }

    bindEvent() {
      this.header = this.header || document.getElementById(this.selectors.header);
      this.wrapper = this.wrapper || this.closest(this.selectors.wrapper);

      if (!this.wrapper) return;

      this.wrapper.removeEventListener('mouseleave', this.onMouseLeaveEvent);
      this.wrapper.addEventListener('mouseleave', this.onMouseLeaveEvent);
    }

    showSublist() {
      const dropdown = this.querySelector(this.selectors.dropdown);
      const background = this.header && this.header.querySelector(this.selectors.background);

      if (!dropdown || !background) return;

      background.style.height = `${this.header.clientHeight + dropdown.clientHeight}px`;
      background.classList.add(this.classes.active);
    }

    addClasses() {
      this.classList.add(this.classes.active);
      this.clearTimeouts();

      const childAnimate = this.querySelectorAll(this.selectors.childAnimate);
      const delay = childAnimate.length > 5 ? 75 : 150;

      childAnimate.forEach((element, index) => {
        const timeout = window.setTimeout(() => {
          if (this.classList.contains(this.classes.active)) {
            element.classList.add(this.classes.animate);
          }
        }, prefersReducedMotion() ? 0 : (delay * index) + 100);

        this.timeouts.push(timeout);
      });

      this.header.classList.add(this.classes.hover);
    }

    removeClasses() {
      if (!this.header) return;

      this.header.querySelectorAll(this.selectors.menus).forEach((menu) => {
        if (typeof menu.reset === 'function') menu.reset();
      });
    }

    clearTimeouts() {
      this.timeouts.forEach((timeout) => window.clearTimeout(timeout));
      this.timeouts = [];
      window.clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }

    reset() {
      this.classList.remove(this.classes.active);

      this.querySelectorAll(this.selectors.childAnimate).forEach((element) => {
        element.classList.remove(this.classes.animate);
      });

      this.clearTimeouts();
    }

    resetBackground() {
      if (!this.header) return;

      const background = this.header.querySelector(this.selectors.background);
      if (background) background.classList.remove(this.classes.active);
    }

    close() {
      this.forceClose();
    }
  }

  if (!customElements.get('mega-menu')) {
    customElements.define('mega-menu', MegaMenu);
  }
})();
